import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSucursal } from '../context/SucursalContext';
import { useAuth } from '../context/AuthContext';

const Layout = ({ children }) => {
  const { sucursales, sucursalActual, cambiarSucursal, loading: sucursalLoading } = useSucursal();
  const { user, logout, isAdmin, isTecnico, isRecepcionista } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Cerrar menú móvil cuando cambia la ruta
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevenir scroll del body cuando el menú está abierto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getRoleLabel = (rol) => {
    const labels = {
      admin: 'Administrador',
      tecnico: 'Tecnico',
      recepcionista: 'Recepcionista'
    };
    return labels[rol] || rol;
  };

  return (
    <div className="app-layout">
      {/* Header Móvil */}
      <header className="mobile-header">
        <button className="mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Abrir menú">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
        <div className="mobile-logo">
          <img
            src="https://intersecom.com/wp-content/uploads/2021/04/cropped-logo-intersecom.png"
            alt="Intersecom"
          />
        </div>
        <div className="mobile-user-avatar" onClick={toggleMobileMenu}>
          {getInitials(user?.nombre)}
        </div>
      </header>

      {/* Overlay para cerrar menú móvil */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img
              src="https://intersecom.com/wp-content/uploads/2021/04/cropped-logo-intersecom.png"
              alt="Intersecom"
              className="sidebar-logo-img"
            />
          </div>
        </div>

        {/* Selector de Sucursal - Solo para admin */}
        {isAdmin() && (
          <div className="sucursal-selector">
            <label>Sucursal:</label>
            <select
              value={sucursalActual?.id || 'todas'}
              onChange={(e) => cambiarSucursal(e.target.value)}
              disabled={sucursalLoading}
            >
              <option value="todas">Todas las Sucursales</option>
              {sucursales.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>
        )}

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">Principal</div>
            <NavLink to="/" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span>Dashboard</span>
            </NavLink>
            {isAdmin() && (
              <NavLink to="/reportes" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
                <span>Reportes</span>
              </NavLink>
            )}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Reparaciones</div>
            <NavLink to="/ordenes" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              <span>Ordenes</span>
            </NavLink>
            {(isAdmin() || isRecepcionista()) && (
              <NavLink to="/ordenes/nueva" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                <span>Nueva Orden</span>
              </NavLink>
            )}
          </div>

          {(isAdmin() || isRecepcionista()) && (
            <div className="nav-section">
              <div className="nav-section-title">Cotizaciones</div>
              <NavLink to="/cotizaciones" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span>Cotizaciones</span>
              </NavLink>
              <NavLink to="/cotizaciones/nueva" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                <span>Nueva Cotizacion</span>
              </NavLink>
            </div>
          )}

          {(isAdmin() || isRecepcionista()) && (
            <div className="nav-section">
              <div className="nav-section-title">Caja</div>
              <NavLink to="/recibos" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                  <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
                </svg>
                <span>Recibos de Caja</span>
              </NavLink>
            </div>
          )}

          {(isAdmin() || isRecepcionista()) && (
            <div className="nav-section">
              <div className="nav-section-title">Gestion</div>
              <NavLink to="/clientes" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <span>Clientes</span>
              </NavLink>
              {isAdmin() && (
                <NavLink to="/empleados" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>Empleados</span>
                </NavLink>
              )}
            </div>
          )}

          {isAdmin() && (
            <div className="nav-section">
              <div className="nav-section-title">Administracion</div>
              <NavLink to="/sucursales" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <path d="M9 22V12h6v10"/>
                </svg>
                <span>Sucursales</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* Info del usuario y logout */}
        <div className="user-info">
          <div className="user-details">
            <div className="user-avatar">
              {getInitials(user?.nombre)}
            </div>
            <div>
              <div className="user-name">{user?.nombre}</div>
              <div className="user-role">
                <span className={`role-badge role-${user?.rol}`}>
                  {getRoleLabel(user?.rol)}
                </span>
              </div>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Cerrar Sesion
          </button>
        </div>
      </aside>

      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;
